import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { Spinner } from '../components/ui/Spinner';
import { APP_CONFIG } from '../config/app';
import MainLayout from '../layouts/MainLayout';
import HomePage from '../pages/HomePage';
import PrivateRoute from './PrivateRoute';

const DepannageInformatique = lazy(() => import('../pages/DepannageInformatique'));
const ReparationTelephone = lazy(() => import('../pages/ReparationTelephone'));
const ReparationTelephoneTarifs = lazy(() => import('../pages/ReparationTelephoneTarifs'));
const CreationSiteWeb = lazy(() => import('../pages/CreationSiteWeb'));
const UserDashboard = lazy(() => import('../pages/UserDashboard'));
const TicketDetail = lazy(() => import('../pages/TicketDetail'));
const ExpertsMap = lazy(() => import('../pages/ExpertsMap'));
const PolitiqueConfidentialite = lazy(() => import('../pages/legal/PolitiqueConfidentialite'));
const MentionsLegales = lazy(() => import('../pages/legal/MentionsLegales'));
const ConditionsUtilisation = lazy(() => import('../pages/legal/ConditionsUtilisation'));
const VerifyEmail = lazy(() => import('../pages/VerifyEmail'));
const BlogIndex = lazy(() => import('../pages/blog/BlogIndex'));
const BlogSilo = lazy(() => import('../pages/blog/BlogSilo'));
const BlogArticle = lazy(() => import('../pages/blog/BlogArticle'));
const DepannageGenevois = lazy(() => import('../pages/local/DepannageGenevois'));
const DepannageSaintJulien = lazy(() => import('../pages/local/DepannageSaintJulien'));
const DepannageAnnemasse = lazy(() => import('../pages/local/DepannageAnnemasse'));
const DepannageAnnecy = lazy(() => import('../pages/local/DepannageAnnecy'));
const DepannageGex = lazy(() => import('../pages/local/DepannageGex'));
const DepannageViry = lazy(() => import('../pages/local/DepannageViry'));
const DepannageValleiry = lazy(() => import('../pages/local/DepannageValleiry'));
const DepannageSaintGenis = lazy(() => import('../pages/local/DepannageSaintGenis'));
const DepannageFerney = lazy(() => import('../pages/local/DepannageFerney'));
const DepannageDivonne = lazy(() => import('../pages/local/DepannageDivonne'));
const DepannagePrevessin = lazy(() => import('../pages/local/DepannagePrevessin'));
const DepannageArchamps = lazy(() => import('../pages/local/DepannageArchamps'));
const DepannageCollonges = lazy(() => import('../pages/local/DepannageCollonges'));
const DepannageOrnex = lazy(() => import('../pages/local/DepannageOrnex'));
const DepannageCessy = lazy(() => import('../pages/local/DepannageCessy'));
const DepannageSegny = lazy(() => import('../pages/local/DepannageSegny'));
const DepannageGeneve = lazy(() => import('../pages/local/DepannageGeneve'));
const ContactPage = lazy(() => import('../pages/ContactPage'));
const ReparationTelephoneViry = lazy(() => import('../pages/local/ReparationTelephoneViry'));
const ReparationTelephoneSaintJulien = lazy(() => import('../pages/local/ReparationTelephoneSaintJulien'));
const ReparationIphoneGeneve = lazy(() => import('../pages/local/ReparationIphoneGeneve'));
const ReparationTelephoneAnnemasse = lazy(() => import('../pages/local/ReparationTelephoneAnnemasse'));

const PageFallback = () => (
  <div className="min-h-screen bg-[#36393F] flex items-center justify-center">
    <Spinner />
  </div>
);

const AppRoutes = () => {
  return (
    <Routes>
      <Route
        element={
          <MainLayout>
            <Suspense fallback={<PageFallback />}>
              <Outlet />
            </Suspense>
          </MainLayout>
        }
      >
        <Route path={APP_CONFIG.routes.home} element={<HomePage />} />
        <Route path={APP_CONFIG.routes.depannage} element={<DepannageInformatique />} />
        <Route path={APP_CONFIG.routes.reparationTelephone} element={<ReparationTelephone />} />
        <Route path={APP_CONFIG.routes.reparationTelephoneTarifs} element={<ReparationTelephoneTarifs />} />
        <Route path={APP_CONFIG.routes.creationSite} element={<CreationSiteWeb />} />
        <Route path={APP_CONFIG.routes.expertsMap} element={<ExpertsMap />} />
        
        {/* Routes protégées */}
        <Route
          path={APP_CONFIG.routes.monEspace}
          element={
            <PrivateRoute>
              <UserDashboard />
            </PrivateRoute>
          }
        />
        <Route
          path={APP_CONFIG.routes.tickets}
          element={
            <PrivateRoute>
              <TicketDetail />
            </PrivateRoute>
          }
        />

        {/* Pages légales */}
        <Route path={APP_CONFIG.routes.politiqueConfidentialite} element={<PolitiqueConfidentialite />} />
        <Route path={APP_CONFIG.routes.mentionsLegales} element={<MentionsLegales />} />
        <Route path={APP_CONFIG.routes.conditionsUtilisation} element={<ConditionsUtilisation />} />

        {/* Blog */}
        <Route path={APP_CONFIG.routes.blog} element={<BlogIndex />} />
        <Route path={APP_CONFIG.routes.blogSilo} element={<BlogSilo />} />
        <Route path={APP_CONFIG.routes.blogArticle} element={<BlogArticle />} />

        {/* Pages SEO locales */}
        <Route path={APP_CONFIG.routes.depannageGenevois} element={<DepannageGenevois />} />
        <Route path={APP_CONFIG.routes.depannageSaintJulien} element={<DepannageSaintJulien />} />
        <Route path={APP_CONFIG.routes.depannageAnnemasse} element={<DepannageAnnemasse />} />
        <Route path={APP_CONFIG.routes.depannageAnnecy} element={<DepannageAnnecy />} />
        <Route path={APP_CONFIG.routes.depannageGex} element={<DepannageGex />} />
        <Route path={APP_CONFIG.routes.depannageViry} element={<DepannageViry />} />
        <Route path={APP_CONFIG.routes.depannageValleiry} element={<DepannageValleiry />} />
        <Route path={APP_CONFIG.routes.depannageSaintGenis} element={<DepannageSaintGenis />} />
        <Route path={APP_CONFIG.routes.depannageFerney} element={<DepannageFerney />} />
        <Route path={APP_CONFIG.routes.depannageDivonne} element={<DepannageDivonne />} />
        <Route path={APP_CONFIG.routes.depannagePrevessin} element={<DepannagePrevessin />} />
        <Route path={APP_CONFIG.routes.depannageArchamps} element={<DepannageArchamps />} />
        <Route path={APP_CONFIG.routes.depannageCollonges} element={<DepannageCollonges />} />
        <Route path={APP_CONFIG.routes.depannageOrnex} element={<DepannageOrnex />} />
        <Route path={APP_CONFIG.routes.depannageCessy} element={<DepannageCessy />} />
        <Route path={APP_CONFIG.routes.depannageSegny} element={<DepannageSegny />} />
        <Route path={APP_CONFIG.routes.depannageGeneve} element={<DepannageGeneve />} />

        {/* Pages SEO locales — réparation téléphone */}
        <Route path={APP_CONFIG.routes.reparationTelephoneViry} element={<ReparationTelephoneViry />} />
        <Route path={APP_CONFIG.routes.reparationTelephoneSaintJulien} element={<ReparationTelephoneSaintJulien />} />
        <Route path={APP_CONFIG.routes.reparationIphoneGeneve} element={<ReparationIphoneGeneve />} />
        <Route path={APP_CONFIG.routes.reparationTelephoneAnnemasse} element={<ReparationTelephoneAnnemasse />} />

        {/* Contact */}
        <Route path={APP_CONFIG.routes.contact} element={<ContactPage />} />

        {/* Vérification d'email */}
        <Route path="/verify-email" element={<VerifyEmail />} />

        {/* Redirection de /dashboard vers /mon-espace */}
        <Route path="/dashboard" element={<Navigate to={APP_CONFIG.routes.monEspace} replace />} />
      </Route>
    </Routes>
  );
};

export default AppRoutes; 